// EmployeePayrollSection.jsx – Version améliorée avec traçabilité, design moderne et modal
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoiceDollar, faPlus, faCheckCircle,
  faDollarSign, faEye, faSyncAlt, faExclamationTriangle,
  faCreditCard, faSpinner, faTimes, faHourglassHalf,
  faFilter, faUndo, faSearch, faCoins, faFileExport,
  faBan, faWallet, faHandHoldingUsd, faUserCheck,
  faMoneyBillWave, faClock, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import FichePaieDetail from './FichePaieDetail';


const API_URL = `${API_CONFIG2.apiUrl}employe/api_paie.php`;

// Constantes
const STATUS_FICHE = {
  BROUILLON: 'Brouillon',
  VALIDE: 'Validé',
  PAYE: 'Payé'
};

const STATUS_AVANCE = {
  DEMANDE: 'Demandé',
  APPROUVE: 'Approuvé',
  REMBOURSE: 'Remboursé',
  ANNULE: 'Annulé'
};

const MODES_PAIEMENT = ['especes', 'virement', 'cheque', 'autre'];

// ------------------ Sous-composants améliorés ------------------
const FichesTable = ({ fiches, onView, onValidate, onPay, loadingIds, isActionLoading }) => (
  <div className="table-responsive">
    <table className="payroll-table">
      <thead>
        <tr>
          <th><FontAwesomeIcon icon={faCalendarAlt} /> Période</th>
          <th><FontAwesomeIcon icon={faMoneyBillWave} /> Salaire brut</th>
          <th><FontAwesomeIcon icon={faHandHoldingUsd} /> Net à payer</th>
          <th>Statut</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {fiches.map(f => (
          <tr key={f.id} className="fade-in">
            <td>{f.periode}</td>
            <td>{parseFloat(f.salaire_brut).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD</td>
            <td>{parseFloat(f.salaire_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD</td>
            <td><span className={`badge-status ${f.statut}`}>{f.statut}</span></td>
            <td>
              <div className="payroll-actions">
                <button onClick={() => onView(f.id)} title="Voir le détail" aria-label="Voir le détail" disabled={isActionLoading(f.id)}>
                  <FontAwesomeIcon icon={faEye} />
                </button>
                {f.statut === STATUS_FICHE.BROUILLON && (
                  <button 
                    onClick={() => onValidate(f.id)} 
                    title="Valider la fiche (déduit le brut de la caisse)" 
                    aria-label="Valider la fiche"
                    className="action-validate" 
                    disabled={isActionLoading(f.id)}
                  >
                    {loadingIds[f.id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />}
                  </button>
                )}
                {f.statut === STATUS_FICHE.VALIDE && (
                  <button 
                    onClick={() => onPay(f.id, f.salaire_net)} 
                    title="Payer la fiche (marque comme payée)" 
                    aria-label="Payer la fiche"
                    className="action-pay" 
                    disabled={isActionLoading(f.id)}
                  >
                    {loadingIds[f.id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDollarSign} />}
                  </button>
                )}
                {f.statut === STATUS_FICHE.PAYE && (
                  <span className="paid-indicator"><FontAwesomeIcon icon={faCheckCircle} /> Payé</span>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AvancesList = ({ avances, onUpdateStatus, onCancel, loadingIds, isActionLoading }) => (
  <ul className="avance-list">
    {avances.map(a => (
      <li key={a.id} className="fade-in">
        <div className="avance-info">
          <span className="avance-montant">{parseFloat(a.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD</span>
          <span className="avance-motif">{a.motif || 'Sans motif'}</span>
          <span className="avance-date"><FontAwesomeIcon icon={faClock} /> {a.date_demande}</span>
          <span className={`badge-status ${a.statut}`}>{a.statut}</span>
        </div>
        <div className="avance-actions">
          {a.statut === STATUS_AVANCE.DEMANDE && (
            <>
              <button 
                className="btn-payroll success" 
                onClick={() => onUpdateStatus(a.id, STATUS_AVANCE.APPROUVE)} 
                disabled={isActionLoading(a.id)}
                title="Approuver et déduire de la caisse"
              >
                {loadingIds[a.id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUserCheck} />} Approuver
              </button>
              <button 
                className="btn-payroll danger" 
                onClick={() => onCancel(a.id)} 
                disabled={isActionLoading(a.id)}
                title="Annuler la demande"
              >
                {loadingIds[a.id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBan} />}
              </button>
            </>
          )}
          {a.statut === STATUS_AVANCE.APPROUVE && (
            <button 
              className="btn-payroll success" 
              onClick={() => onUpdateStatus(a.id, STATUS_AVANCE.REMBOURSE)} 
              disabled={isActionLoading(a.id)}
              title="Marquer comme remboursé"
            >
              {loadingIds[a.id] ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Rembourser'}
            </button>
          )}
          {a.statut === STATUS_AVANCE.ANNULE && (
            <span className="text-muted">Annulée</span>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const AvanceForm = ({ newAvance, setNewAvance, onSubmit, loading, disabled }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="avance-form">
      <div className="form-group">
        <label htmlFor="montant">Montant (USD)</label>
        <input
          id="montant"
          type="number"
          placeholder="0"
          value={newAvance.montant}
          onChange={(e) => setNewAvance({...newAvance, montant: e.target.value})}
          className="glass-input"
          disabled={disabled}
          required
          min="1"
          step="1"
        />
      </div>
      <div className="form-group">
        <label htmlFor="motif">Motif (optionnel)</label>
        <input
          id="motif"
          type="text"
          placeholder="Ex: Achat fournitures"
          value={newAvance.motif}
          onChange={(e) => setNewAvance({...newAvance, motif: e.target.value})}
          className="glass-input"
          disabled={disabled}
        />
      </div>
      <div className="form-group">
        <label htmlFor="date">Date de la demande</label>
        <input
          id="date"
          type="date"
          value={newAvance.date_demande}
          onChange={(e) => setNewAvance({...newAvance, date_demande: e.target.value})}
          className="glass-input"
          disabled={disabled}
        />
      </div>
      <button className="btn-payroll primary full-width" type="submit" disabled={disabled || loading}>
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
        Demander l'avance
      </button>
    </form>
  );
};

// ---------- Modal personnalisé (intégré directement) ----------
const Modal = ({ isOpen, onClose, title, children, confirmLabel, onConfirm, cancelLabel, onCancel, type }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4>{title}</h4>
          <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">
          {onCancel && <button className="btn-payroll secondary" onClick={onCancel}>{cancelLabel || 'Annuler'}</button>}
          {onConfirm && <button className={`btn-payroll ${type === 'danger' ? 'danger' : 'primary'}`} onClick={onConfirm}>{confirmLabel || 'Confirmer'}</button>}
        </div>
      </div>
    </div>
  );
};

// ------------------ Composant principal ------------------
const EmployeePayrollSection = ({ employeId, user_id }) => {
  // États
  const [fiches, setFiches] = useState([]);
  const [avances, setAvances] = useState([]);
  const [soldeCaisse, setSoldeCaisse] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ficheIdDetail, setFicheIdDetail] = useState(null);
  const [newAvance, setNewAvance] = useState({ montant: '', motif: '', date_demande: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});
  const [notification, setNotification] = useState(null);

  // États pour les filtres
  const [ficheFilterStatut, setFicheFilterStatut] = useState('all');
  const [ficheFilterPeriode, setFicheFilterPeriode] = useState('');
  const [avanceFilterStatut, setAvanceFilterStatut] = useState('all');
  const [avanceSearchMotif, setAvanceSearchMotif] = useState('');

  // États pour le modal
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmer',
    cancelLabel: 'Annuler',
    onConfirm: null,
    onCancel: null,
    type: 'primary',
    input: null, // pour saisie personnalisée (mode paiement)
  });

  // Ref pour AbortController
  const abortControllerRef = useRef(null);

  const getCurrentPeriode = useCallback(() => {
    const now = new Date();
    const mois = String(now.getMonth() + 1).padStart(2, '0');
    const annee = now.getFullYear();
    return `${annee}-${mois}`;
  }, []);

  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Chargement des données
  const loadData = useCallback(async () => {
    if (!employeId) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    setError(null);
    setInitialLoading(true);
    try {
      const uniqueParam = new Date().getTime();
      const [resFiches, resAvances, resSolde] = await Promise.all([
        fetch(`${API_URL}?action=get_fiches&employe_id=${employeId}&unique=${uniqueParam}`, { signal }),
        fetch(`${API_URL}?action=get_avances&employe_id=${employeId}&unique=${uniqueParam}`, { signal }),
        fetch(`${API_URL}?action=get_solde_caisse&unique=${uniqueParam}`, { signal })
      ]);
      
      if (!resFiches.ok || !resAvances.ok || !resSolde.ok) {
        throw new Error('Erreur réseau lors du chargement');
      }

      const dataFiches = await resFiches.json();
      const dataAvances = await resAvances.json();
      const dataSolde = await resSolde.json();

      if (dataFiches.status === 'success') setFiches(dataFiches.fiches);
      else throw new Error(dataFiches.message || 'Erreur chargement fiches');
      if (dataAvances.status === 'success') setAvances(dataAvances.avances);
      else throw new Error(dataAvances.message || 'Erreur chargement avances');
      if (dataSolde.status === 'success') setSoldeCaisse(dataSolde.solde);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        showNotification('error', err.message);
      }
    } finally {
      if (!signal.aborted) {
        setInitialLoading(false);
      }
    }
  }, [employeId, showNotification]);

  useEffect(() => {
    loadData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  // Mise à jour optimiste
  const optimisticUpdate = (stateSetter, itemId, updater) => {
    stateSetter(prev => prev.map(item => item.id === itemId ? updater(item) : item));
  };

  // Ouvrir le modal
  const openModal = (options) => {
    setModal(prev => ({ ...prev, isOpen: true, ...options }));
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Générer une fiche
  const genererFiche = async () => {
    if (initialLoading) return;
    const periode = getCurrentPeriode();
    const exist = fiches.find(f => f.periode === periode);
    if (exist) {
      showNotification('error', `Une fiche existe déjà pour la période ${periode}`);
      return;
    }
    openModal({
      title: 'Confirmation',
      message: `Générer la fiche de paie pour la période ${periode} ?`,
      confirmLabel: 'Générer',
      onConfirm: async () => {
        closeModal();
        setActionLoading(true);
        try {
          const response = await fetch(`${API_URL}?action=create_fiche`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ employe_id: employeId, periode })
          });
          const data = await response.json();
          if (data.status === 'success') {
            showNotification('success', 'Fiche créée avec succès');
            await loadData();
          } else {
            throw new Error(data.message || 'Erreur création');
          }
        } catch (err) {
          showNotification('error', err.message);
        } finally {
          setActionLoading(false);
        }
      },
      onCancel: closeModal
    });
  };

  // Valider une fiche (avec user_id)
  const validerFiche = async (ficheId) => {
    if (initialLoading) return;
    openModal({
      title: 'Valider la fiche',
      message: 'Cette action déduira le salaire brut de la caisse. Confirmer ?',
      confirmLabel: 'Valider',
      type: 'primary',
      onConfirm: async () => {
        closeModal();
        const oldFiches = [...fiches];
        optimisticUpdate(setFiches, ficheId, f => ({ ...f, statut: STATUS_FICHE.VALIDE }));
        setLoadingActions(prev => ({ ...prev, [ficheId]: true }));
        try {
          const response = await fetch(`${API_URL}?action=valider_fiche`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: ficheId, user_id: user_id || null })
          });
          const data = await response.json();
          if (data.status === 'success') {
            showNotification('success', 'Fiche validée');
            if (data.nouveau_solde !== undefined) setSoldeCaisse(data.nouveau_solde);
            await loadData();
          } else {
            throw new Error(data.message || 'Erreur validation');
          }
        } catch (err) {
          setFiches(oldFiches);
          showNotification('error', err.message);
        } finally {
          setLoadingActions(prev => ({ ...prev, [ficheId]: false }));
        }
      },
      onCancel: closeModal
    });
  };

  // Payer une fiche
  const payerFiche = async (ficheId, montant) => {
    if (initialLoading) return;
    openModal({
      title: 'Paiement de la fiche',
      message: `Confirmer le paiement de ${montant.toFixed(2)} USD ?`,
      confirmLabel: 'Payer',
      type: 'primary',
      input: {
        type: 'select',
        label: 'Mode de paiement',
        options: MODES_PAIEMENT,
        defaultValue: 'especes'
      },
      onConfirm: async (mode) => {
        closeModal();
        if (!mode || !MODES_PAIEMENT.includes(mode.toLowerCase())) {
          showNotification('error', 'Mode de paiement invalide');
          return;
        }
        const oldFiches = [...fiches];
        optimisticUpdate(setFiches, ficheId, f => ({ ...f, statut: STATUS_FICHE.PAYE }));
        setLoadingActions(prev => ({ ...prev, [ficheId]: true }));
        try {
          const response = await fetch(`${API_URL}?action=payer_fiche`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              id: ficheId, 
              mode: mode.toLowerCase(),
              user_id: user_id || null
            })
          });
          const data = await response.json();
          if (data.status === 'success') {
            showNotification('success', `Paiement enregistré. Nouveau solde: ${data.nouveau_solde?.toLocaleString('fr-FR')} USD`);
            if (data.nouveau_solde !== undefined) setSoldeCaisse(data.nouveau_solde);
            await loadData();
          } else {
            throw new Error(data.message || 'Erreur paiement');
          }
        } catch (err) {
          setFiches(oldFiches);
          showNotification('error', err.message);
        } finally {
          setLoadingActions(prev => ({ ...prev, [ficheId]: false }));
        }
      },
      onCancel: closeModal
    });
  };

  // Ajouter une avance
  const ajouterAvance = async () => {
    if (initialLoading) return;
    const montant = parseFloat(newAvance.montant);
    if (!montant || montant <= 0) {
      showNotification('error', 'Montant invalide (doit être un nombre positif)');
      return;
    }
    const dateDemande = newAvance.date_demande || new Date().toISOString().split('T')[0];
    const motif = newAvance.motif.trim() || 'Sans motif';
    const tempId = -Date.now();
    const newAvanceObj = {
      id: tempId,
      montant,
      motif,
      date_demande: dateDemande,
      statut: STATUS_AVANCE.DEMANDE
    };
    setAvances(prev => [newAvanceObj, ...prev]);
    setNewAvance({ montant: '', motif: '', date_demande: '' });
    setLoadingActions(prev => ({ ...prev, ['avance_temp']: true }));
    try {
      const response = await fetch(`${API_URL}?action=create_avance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employe_id: employeId,
          montant,
          date_demande: dateDemande,
          motif
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        showNotification('success', 'Demande d\'avance enregistrée');
        await loadData();
      } else {
        throw new Error(data.message || 'Erreur création avance');
      }
    } catch (err) {
      setAvances(prev => prev.filter(a => a.id !== tempId));
      showNotification('error', err.message);
    } finally {
      setLoadingActions(prev => ({ ...prev, ['avance_temp']: false }));
    }
  };

  // Mettre à jour le statut d'une avance (avec user_id)
  const updateAvance = async (id, statut) => {
    if (initialLoading) return;
    const confirmMsg = statut === STATUS_AVANCE.APPROUVE 
      ? `Approuver cette avance ? (le montant sera déduit de la caisse)`
      : `Changer le statut en "${statut}" ?`;
    openModal({
      title: 'Confirmation',
      message: confirmMsg,
      confirmLabel: 'Confirmer',
      type: statut === STATUS_AVANCE.APPROUVE ? 'primary' : 'secondary',
      onConfirm: async () => {
        closeModal();
        const oldAvances = [...avances];
        optimisticUpdate(setAvances, id, a => ({ ...a, statut }));
        setLoadingActions(prev => ({ ...prev, [id]: true }));
        try {
          const response = await fetch(`${API_URL}?action=update_avance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, statut, user_id: user_id || null })
          });
          const data = await response.json();
          if (data.status === 'success') {
            showNotification('success', `Avance ${statut}`);
            if (data.nouveau_solde !== undefined) setSoldeCaisse(data.nouveau_solde);
            await loadData();
          } else {
            throw new Error(data.message || 'Erreur mise à jour');
          }
        } catch (err) {
          setAvances(oldAvances);
          showNotification('error', err.message);
        } finally {
          setLoadingActions(prev => ({ ...prev, [id]: false }));
        }
      },
      onCancel: closeModal
    });
  };

  // Annuler une avance
  const annulerAvance = async (id) => {
    await updateAvance(id, STATUS_AVANCE.ANNULE);
  };

  const afficherFiche = (ficheId) => {
    setFicheIdDetail(ficheId);
  };

  // Filtres
  const filteredFiches = useMemo(() => {
    let result = [...fiches];
    if (ficheFilterStatut !== 'all') {
      result = result.filter(f => f.statut === ficheFilterStatut);
    }
    if (ficheFilterPeriode) {
      result = result.filter(f => f.periode.startsWith(ficheFilterPeriode));
    }
    result.sort((a, b) => b.periode.localeCompare(a.periode));
    return result;
  }, [fiches, ficheFilterStatut, ficheFilterPeriode]);

  const filteredAvances = useMemo(() => {
    let result = [...avances];
    if (avanceFilterStatut !== 'all') {
      result = result.filter(a => a.statut === avanceFilterStatut);
    }
    if (avanceSearchMotif.trim() !== '') {
      const search = avanceSearchMotif.trim().toLowerCase();
      result = result.filter(a => (a.motif || '').toLowerCase().includes(search));
    }
    result.sort((a, b) => (a.date_demande < b.date_demande ? 1 : -1));
    return result;
  }, [avances, avanceFilterStatut, avanceSearchMotif]);

  const resetFilters = () => {
    setFicheFilterStatut('all');
    setFicheFilterPeriode('');
    setAvanceFilterStatut('all');
    setAvanceSearchMotif('');
  };

  const exportCSV = () => {
    if (filteredFiches.length === 0) {
      showNotification('error', 'Aucune fiche à exporter');
      return;
    }
    const headers = ['Période', 'Salaire brut', 'Net à payer', 'Statut'];
    const rows = filteredFiches.map(f => [
      f.periode,
      parseFloat(f.salaire_brut).toFixed(2),
      parseFloat(f.salaire_net).toFixed(2),
      f.statut
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fiches_paie_${employeId}_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Indicateur de chargement
  if (initialLoading && fiches.length === 0 && avances.length === 0) {
    return (
      <div className="payroll-section glass" style={{ textAlign: 'center', padding: '3rem' }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#3b82f6" />
        <p style={{ marginTop: '1rem' }}>Chargement des données de paie...</p>
      </div>
    );
  }

  if (error && fiches.length === 0 && avances.length === 0) {
    return (
      <div className="payroll-section glass" style={{ padding: '1.5rem', color: '#ef4444' }}>
        <FontAwesomeIcon icon={faExclamationTriangle} /> Erreur : {error}
      </div>
    );
  }

  const isActionLoading = (id) => loadingActions[id] || false;

  // Rendu
  return (
    <div className="payroll-section glass">
      <style jsx>{`
        /* ----- Styles améliorés ----- */
        .payroll-section {
          margin-top: 2rem;
          padding: 1.5rem 2rem;
          border-radius: 24px;
          background: rgba(10, 14, 39, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          box-shadow: 0 12px 48px rgba(0,0,0,0.5);
          color: #e0e7ff;
          position: relative;
          transition: all 0.3s ease;
        }

        .payroll-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .payroll-header h3 {
          margin: 0;
          font-size: 1.6rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .payroll-header h3 svg {
          color: #3b82f6;
        }

        .solde-caisse {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(34, 197, 94, 0.12);
          padding: 0.4rem 1.2rem;
          border-radius: 40px;
          border: 1px solid rgba(34, 197, 94, 0.25);
          color: #4ade80;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 0 20px rgba(34, 197, 94, 0.05);
        }
        .solde-caisse:hover {
          background: rgba(34, 197, 94, 0.18);
          transform: scale(1.02);
        }
        .solde-caisse svg {
          font-size: 1.2rem;
        }

        .header-actions {
          display: flex;
          gap: 0.6rem;
          flex-wrap: wrap;
        }

        .btn-payroll {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          color: #e0e7ff;
          padding: 0.5rem 1.2rem;
          border-radius: 40px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 500;
          white-space: nowrap;
        }
        .btn-payroll:hover:not(:disabled) {
          background: rgba(255,255,255,0.12);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        .btn-payroll:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          transform: none;
        }
        .btn-payroll.primary {
          border-color: #3b82f6;
          color: #60a5fa;
          background: rgba(59,130,246,0.08);
        }
        .btn-payroll.primary:hover:not(:disabled) {
          background: rgba(59,130,246,0.18);
        }
        .btn-payroll.success {
          border-color: #22c55e;
          color: #4ade80;
          background: rgba(34,197,94,0.08);
        }
        .btn-payroll.success:hover:not(:disabled) {
          background: rgba(34,197,94,0.18);
        }
        .btn-payroll.danger {
          border-color: #ef4444;
          color: #f87171;
          background: rgba(239,68,68,0.08);
        }
        .btn-payroll.danger:hover:not(:disabled) {
          background: rgba(239,68,68,0.18);
        }
        .btn-payroll.secondary {
          border-color: #6b7280;
          color: #9ca3af;
        }
        .btn-payroll.secondary:hover:not(:disabled) {
          background: rgba(107,114,128,0.18);
        }
        .btn-payroll.export {
          border-color: #8b5cf6;
          color: #a78bfa;
          background: rgba(139,92,246,0.08);
        }
        .btn-payroll.export:hover:not(:disabled) {
          background: rgba(139,92,246,0.18);
        }
        .btn-payroll.full-width {
          width: 100%;
          justify-content: center;
        }

        /* Table */
        .table-responsive {
          overflow-x: auto;
          margin-top: 0.5rem;
        }
        .payroll-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }
        .payroll-table th {
          text-align: left;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          padding: 0.7rem 0.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .payroll-table th svg {
          margin-right: 0.3rem;
          opacity: 0.6;
        }
        .payroll-table td {
          padding: 0.7rem 0.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          vertical-align: middle;
        }
        .payroll-table tr:hover {
          background: rgba(255,255,255,0.03);
        }
        .payroll-table .fade-in {
          animation: fadeIn 0.3s ease;
        }

        .badge-status {
          padding: 0.2rem 0.9rem;
          border-radius: 40px;
          font-size: 0.7rem;
          font-weight: 600;
          display: inline-block;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .badge-status.Brouillon {
          background: rgba(251,191,36,0.15);
          color: #fbbf24;
        }
        .badge-status.Validé {
          background: rgba(59,130,246,0.15);
          color: #60a5fa;
        }
        .badge-status.Payé {
          background: rgba(34,197,94,0.15);
          color: #4ade80;
        }
        .badge-status.Demandé {
          background: rgba(251,191,36,0.15);
          color: #fbbf24;
        }
        .badge-status.Approuvé {
          background: rgba(59,130,246,0.15);
          color: #60a5fa;
        }
        .badge-status.Remboursé {
          background: rgba(34,197,94,0.15);
          color: #4ade80;
        }
        .badge-status.Annulé {
          background: rgba(239,68,68,0.15);
          color: #f87171;
        }

        .payroll-actions {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
          align-items: center;
        }
        .payroll-actions button {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 0.3rem 0.6rem;
          border-radius: 8px;
          transition: 0.2s;
          font-size: 1rem;
        }
        .payroll-actions button:hover:not(:disabled) {
          background: rgba(255,255,255,0.06);
          color: #e0e7ff;
        }
        .payroll-actions button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .payroll-actions .action-validate { color: #22c55e; }
        .payroll-actions .action-validate:hover:not(:disabled) { color: #4ade80; }
        .payroll-actions .action-pay { color: #3b82f6; }
        .payroll-actions .action-pay:hover:not(:disabled) { color: #60a5fa; }
        .paid-indicator {
          color: #22c55e;
          font-size: 0.8rem;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }

        /* Avances */
        .avance-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .avance-list li {
          display: flex;
          justify-content: space-between;
          padding: 0.6rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .avance-list li.fade-in {
          animation: fadeIn 0.3s ease;
        }
        .avance-info {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem 1rem;
          flex: 1;
        }
        .avance-montant {
          font-weight: 600;
          color: #fbbf24;
        }
        .avance-motif {
          color: #cbd5e1;
          font-size: 0.9rem;
        }
        .avance-date {
          font-size: 0.75rem;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }
        .avance-actions {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
        }
        .avance-actions .btn-payroll {
          padding: 0.2rem 0.8rem;
          font-size: 0.7rem;
        }

        /* Formulaire avance */
        .avance-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }
        .form-group label {
          font-size: 0.8rem;
          color: #94a3b8;
          font-weight: 500;
        }
        .glass-input {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.6rem 1rem;
          border-radius: 14px;
          color: #e0e7ff;
          width: 100%;
          transition: 0.2s;
          font-size: 0.9rem;
        }
        .glass-input:focus {
          outline: none;
          border-color: #3b82f6;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .glass-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Filtres */
        .filter-group {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
          margin-bottom: 1.5rem;
          background: rgba(255,255,255,0.03);
          padding: 0.75rem 1rem;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .filter-group label {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.8rem;
          color: #94a3b8;
        }
        .filter-group select, .filter-group input {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.3rem 0.8rem;
          border-radius: 10px;
          color: #e0e7ff;
          font-size: 0.8rem;
          min-height: 2rem;
        }
        .filter-group select:focus, .filter-group input:focus {
          outline: none;
          border-color: #3b82f6;
        }
        .filter-group .btn-reset {
          background: rgba(255,255,255,0.05);
          border: none;
          color: #94a3b8;
          padding: 0.3rem 0.8rem;
          border-radius: 10px;
          cursor: pointer;
          transition: 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }
        .filter-group .btn-reset:hover {
          background: rgba(255,255,255,0.1);
          color: #e0e7ff;
        }
        .search-icon {
          color: #94a3b8;
        }

        .divider {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.06);
          margin: 2rem 0;
        }

        .text-muted {
          color: #94a3b8;
          font-size: 0.8rem;
        }

        /* Notification */
        .notification {
          position: fixed;
          top: 24px;
          right: 24px;
          padding: 0.8rem 1.5rem;
          border-radius: 16px;
          background: rgba(10,14,39,0.92);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          z-index: 9999;
          display: flex;
          align-items: center;
          gap: 1rem;
          animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 12px 40px rgba(0,0,0,0.6);
          max-width: 90vw;
        }
        .notification.success { border-left: 4px solid #22c55e; }
        .notification.error { border-left: 4px solid #ef4444; }
        .notification button {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 0.2rem;
          transition: 0.2s;
        }
        .notification button:hover { color: #e0e7ff; }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.2s ease;
        }
        .modal-content {
          background: rgba(20, 26, 56, 0.95);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 1.5rem 2rem;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 24px 64px rgba(0,0,0,0.6);
          animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .modal-header h4 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.4rem;
          cursor: pointer;
          transition: 0.2s;
        }
        .modal-close:hover { color: #e0e7ff; }
        .modal-body {
          margin: 1rem 0;
          color: #cbd5e1;
          line-height: 1.6;
        }
        .modal-body .modal-input {
          margin-top: 0.8rem;
          width: 100%;
          padding: 0.5rem;
          border-radius: 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          color: #e0e7ff;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.8rem;
          margin-top: 1.5rem;
        }
        .modal-footer .btn-payroll {
          padding: 0.4rem 1.2rem;
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .payroll-section { padding: 1rem; }
          .payroll-header h3 { font-size: 1.2rem; }
          .solde-caisse { font-size: 0.8rem; padding: 0.2rem 0.8rem; }
          .filter-group { flex-direction: column; align-items: stretch; }
          .filter-group label { flex-wrap: wrap; }
          .avance-info { flex-direction: column; align-items: flex-start; gap: 0.2rem; }
          .modal-content { padding: 1rem; }
          .header-actions { width: 100%; }
          .header-actions .btn-payroll { flex: 1; justify-content: center; }
        }
        @media (max-width: 480px) {
          .payroll-actions button { padding: 0.2rem 0.4rem; font-size: 0.8rem; }
          .btn-payroll { font-size: 0.75rem; padding: 0.3rem 0.8rem; }
        }
      `}</style>

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`} role="alert">
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)} aria-label="Fermer"><FontAwesomeIcon icon={faTimes} /></button>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        confirmLabel={modal.confirmLabel}
        onConfirm={() => {
          if (modal.input) {
            // Si le modal a un champ de saisie (select)
            const select = document.getElementById('modal-select');
            const value = select ? select.value : modal.input.defaultValue;
            modal.onConfirm && modal.onConfirm(value);
          } else {
            modal.onConfirm && modal.onConfirm();
          }
        }}
        cancelLabel={modal.cancelLabel}
        onCancel={modal.onCancel}
        type={modal.type}
      >
        <p>{modal.message}</p>
        {modal.input && modal.input.type === 'select' && (
          <div>
            <label htmlFor="modal-select" style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', color: '#94a3b8' }}>
              {modal.input.label}
            </label>
            <select id="modal-select" className="glass-input" defaultValue={modal.input.defaultValue}>
              {modal.input.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}
      </Modal>

      {/* Overlay de chargement global */}
      {actionLoading && (
        <div className="loading-overlay" aria-hidden="true">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#3b82f6" />
        </div>
      )}

      <div className="payroll-header">
        <h3>
          <FontAwesomeIcon icon={faFileInvoiceDollar} /> Fiches de paie & Avances
          {soldeCaisse !== null && (
            <span className="solde-caisse">
              <FontAwesomeIcon icon={faCoins} /> Solde caisse: {soldeCaisse.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} USD
            </span>
          )}
          {initialLoading && (
            <span className="initial-loading-badge">
              <FontAwesomeIcon icon={faHourglassHalf} spin /> Mise à jour...
            </span>
          )}
        </h3>
        <div className="header-actions">
          <button className="btn-payroll" onClick={loadData} disabled={initialLoading || actionLoading}>
            {initialLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSyncAlt} />}
            {initialLoading ? ' Chargement...' : ' Rafraîchir'}
          </button>
          <button className="btn-payroll export" onClick={exportCSV} disabled={filteredFiches.length === 0}>
            <FontAwesomeIcon icon={faFileExport} /> Exporter CSV
          </button>
          <button className="btn-payroll primary" onClick={genererFiche} disabled={initialLoading || actionLoading}>
            {actionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
            Générer fiche du mois
          </button>
        </div>
      </div>

      {/* Filtres fiches */}
      <div className="filter-group">
        <label><FontAwesomeIcon icon={faFilter} /> Statut :
          <select value={ficheFilterStatut} onChange={(e) => setFicheFilterStatut(e.target.value)}>
            <option value="all">Tous</option>
            <option value={STATUS_FICHE.BROUILLON}>Brouillon</option>
            <option value={STATUS_FICHE.VALIDE}>Validé</option>
            <option value={STATUS_FICHE.PAYE}>Payé</option>
          </select>
        </label>
        <label>Période :
          <input type="month" value={ficheFilterPeriode} onChange={(e) => setFicheFilterPeriode(e.target.value)} />
        </label>
        <button className="btn-reset" onClick={() => { setFicheFilterStatut('all'); setFicheFilterPeriode(''); }}>
          <FontAwesomeIcon icon={faUndo} /> Réinitialiser
        </button>
      </div>

      {filteredFiches.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>Aucune fiche correspondant aux critères.</p>
      ) : (
        <FichesTable
          fiches={filteredFiches}
          onView={afficherFiche}
          onValidate={validerFiche}
          onPay={payerFiche}
          loadingIds={loadingActions}
          isActionLoading={isActionLoading}
        />
      )}

      <hr className="divider" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        <div>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faCreditCard} /> Avances sur salaire
          </h4>
          <div className="filter-group" style={{ marginBottom: '0.5rem' }}>
            <label>Statut :
              <select value={avanceFilterStatut} onChange={(e) => setAvanceFilterStatut(e.target.value)}>
                <option value="all">Tous</option>
                <option value={STATUS_AVANCE.DEMANDE}>Demandé</option>
                <option value={STATUS_AVANCE.APPROUVE}>Approuvé</option>
                <option value={STATUS_AVANCE.REMBOURSE}>Remboursé</option>
                <option value={STATUS_AVANCE.ANNULE}>Annulé</option>
              </select>
            </label>
            <label>
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input type="text" placeholder="Rechercher par motif..." value={avanceSearchMotif} onChange={(e) => setAvanceSearchMotif(e.target.value)} style={{ width: '150px' }} />
            </label>
            <button className="btn-reset" onClick={() => { setAvanceFilterStatut('all'); setAvanceSearchMotif(''); }}>
              <FontAwesomeIcon icon={faUndo} />
            </button>
          </div>
          {filteredAvances.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>Aucune avance correspondante.</p>
          ) : (
            <AvancesList
              avances={filteredAvances}
              onUpdateStatus={updateAvance}
              onCancel={annulerAvance}
              loadingIds={loadingActions}
              isActionLoading={isActionLoading}
            />
          )}
        </div>

        <div>
          <h5 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faPlus} /> Demander une avance
          </h5>
          <AvanceForm
            newAvance={newAvance}
            setNewAvance={setNewAvance}
            onSubmit={ajouterAvance}
            loading={loadingActions['avance_temp']}
            disabled={initialLoading}
          />
        </div>
      </div>

      {ficheIdDetail && (
        <FichePaieDetail ficheId={ficheIdDetail} onClose={() => setFicheIdDetail(null)} />
      )}

      {/* Style pour l'overlay de chargement global (déjà présent dans le CSS) */}
      <style jsx>{`
        .loading-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10,14,39,0.4);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 24px;
          z-index: 20;
        }
        .initial-loading-badge {
          background: rgba(255,255,255,0.05);
          padding: 0.2rem 0.8rem;
          border-radius: 40px;
          font-size: 0.7rem;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }
      `}</style>
    </div>
  );
};

export default EmployeePayrollSection;